import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';
import { ThemeProvider, createTheme, alpha } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';
import { tokens, fontStack, fontImport } from '../theme/tokens';
import { searchFieldOutlinedInputStyles } from '../../../components/ui/searchFieldStyles';

const ThemeSettingsContext = createContext(null);

export const useThemeSettings = () => useContext(ThemeSettingsContext);

const STORAGE_KEY = 'studentThemeSettings';

// Public API is unchanged from the original context (darkMode, primaryColor,
// updateSettings) so nothing outside this file needs to change. What's new
// is the theme itself: a distinct type scale (Sora for display, Inter for
// body/UI), a "campus" palette (see src/theme/tokens.js), rounder shape,
// tinted shadows instead of flat grey, and global keyframes/scrollbar styling
// shared by every page's animations.
export const ThemeSettingsProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(false);
  const [primaryColor, setPrimaryColor] = useState(tokens.indigo);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (saved) {
        if (typeof saved.darkMode === 'boolean') setDarkMode(saved.darkMode);
        if (saved.primaryColor) setPrimaryColor(saved.primaryColor);
      }
    } catch {
      // ignore corrupt storage
    }
  }, []);

  const updateSettings = ({ darkMode: dm, primaryColor: pc }) => {
    const next = {
      darkMode: dm !== undefined ? dm : darkMode,
      primaryColor: pc !== undefined ? pc : primaryColor,
    };
    setDarkMode(next.darkMode);
    setPrimaryColor(next.primaryColor);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  };

  const theme = useMemo(() => {
    const mode = darkMode ? 'dark' : 'light';
    return createTheme({
      palette: {
        mode,
        primary: { main: primaryColor },
        secondary: { main: tokens.amber },
        success: { main: tokens.mint },
        error: { main: tokens.coral },
        warning: { main: tokens.amber },
        background: {
          default: darkMode ? tokens.cloudDark : tokens.cloud,
          paper: darkMode ? tokens.surfaceDark : '#FFFFFF',
        },
        text: {
          primary: darkMode ? '#F1F0FA' : tokens.ink,
          secondary: darkMode ? alpha('#F1F0FA', 0.65) : tokens.inkSoft,
          disabled: darkMode ? '#AAB4C3' : '#64748B',
        },
        divider: darkMode ? alpha('#FFFFFF', 0.08) : alpha(tokens.ink, 0.08),
      },
      shape: { borderRadius: 14 },
      typography: {
        fontFamily: fontStack.body,
        h1: { fontFamily: fontStack.display }, h2: { fontFamily: fontStack.display },
        h3: { fontFamily: fontStack.display }, h4: { fontFamily: fontStack.display },
        h5: { fontFamily: fontStack.display, fontWeight: 700 },
        h6: { fontFamily: fontStack.display, fontWeight: 700 },
        subtitle1: { fontFamily: fontStack.display, fontWeight: 600 },
        button: { fontFamily: fontStack.body, fontWeight: 600, textTransform: 'none' },
      },
      shadows: [
        'none',
        `0 2px 8px ${alpha(tokens.ink, 0.06)}`,
        `0 4px 14px ${alpha(tokens.ink, 0.07)}`,
        ...Array(22).fill(`0 12px 32px ${alpha(tokens.ink, 0.10)}`),
      ],
      components: {
        MuiCssBaseline: {
          styleOverrides: { body: { transition: 'background-color 0.3s ease' } },
        },
        MuiPaper: {
          styleOverrides: { root: { backgroundImage: 'none' } },
        },
        MuiButton: {
          styleOverrides: {
            root: ({ theme }) => ({
              borderRadius: 12,
              paddingInline: 18,
              '&&[data-app-cancel="true"]': {
                color:
                  theme.palette.mode === 'dark'
                    ? '#111827'
                    : theme.palette.primary.contrastText,
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? '#fff'
                    : theme.palette.primary.main,
                border: `1px solid ${
                  theme.palette.mode === 'dark'
                    ? '#fff'
                    : theme.palette.primary.main
                }`,
                '&:hover': {
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? theme.palette.grey[200]
                      : theme.palette.primary.dark,
                  borderColor:
                    theme.palette.mode === 'dark'
                      ? theme.palette.grey[200]
                      : theme.palette.primary.dark,
                },
                '&.Mui-disabled': { opacity: 0.6 },
              },
            }),
            contained: { boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
          },
        },
        MuiChip: {
          styleOverrides: { root: { fontWeight: 600, borderRadius: 8 } },
        },
        MuiTableCell: {
          styleOverrides: { root: { borderColor: darkMode ? alpha('#FFFFFF', 0.08) : alpha(tokens.ink, 0.06) } },
        },
        MuiAppBar: {
          styleOverrides: { root: { boxShadow: 'none' } },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: ({ theme }) => searchFieldOutlinedInputStyles(theme),
          },
        },
        MuiInputLabel: {
          styleOverrides: {
            root: ({ theme }) => ({
              color: theme.palette.text.primary,
              '&.Mui-focused': {
                color: theme.palette.primary.main,
              },
              '&.Mui-error': {
                color: theme.palette.error.main,
              },
              '&.Mui-disabled': {
                color: theme.palette.text.disabled,
              },
            }),
          },
        },
      },
    });
  }, [darkMode, primaryColor]);

  // A plain CSS string (rather than the JSS object form) so the @import and
  // @keyframes rules come through exactly as written.
  const globalStyles = (
    <GlobalStyles
      styles={`
        ${fontImport}
        :root {
          --font-display: ${fontStack.display};
          --font-body: ${fontStack.body};
          color-scheme: ${darkMode ? 'dark' : 'light'};
        }
        *::selection { background-color: ${alpha(primaryColor, 0.25)}; }
        *::-webkit-scrollbar { width: 8px; height: 8px; }
        *::-webkit-scrollbar-track { background: transparent; }
        *::-webkit-scrollbar-thumb { background: ${alpha(tokens.ink, 0.18)}; border-radius: 8px; }
        @keyframes floatSlow {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(0, -12px); }
        }
        @keyframes shimmer {
          0% { background-position: -400px 0; }
          100% { background-position: 400px 0; }
        }
      `}
    />
  );

  return (
    <ThemeSettingsContext.Provider value={{ darkMode, primaryColor, updateSettings }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {globalStyles}
        {children}
      </ThemeProvider>
    </ThemeSettingsContext.Provider>
  );
};
